import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { useHover } from '../UNSAFE_useHover';
import { useActive } from '../UNSAFE_useActive';
import { useFocus } from '../UNSAFE_useFocus';
import { usePress } from '../UNSAFE_usePress';

export type ActionableOptions = {
  isDisabled?: boolean;
  isRepeat?: boolean;
};

const DefaultActionableOptions = {
  isDisabled: false,
  isRepeat: false
};

/**
 * A hook that can add actionable support to a target element, turning it into a
 * clickable button, div, card, etc.
 * If isHover, isFocus, and isActive are only used for changing visual rendering, it would be
 * faster to not use this hook, and instead use :hover, :focus-visible, :active and usePress.
 * @param onActionHandler
 * @returns
 */
export function useActionable(
  onActionHandler: (event: Event) => void,
  settings: ActionableOptions = DefaultActionableOptions
): { isActive: boolean; isHover: boolean; isFocus: boolean; actionableProps: Record<string, any> } {
  const mergedSettings = { ...DefaultActionableOptions, ...settings };
  const { pressProps } = usePress(onActionHandler, mergedSettings);
  const { hoverProps, isHover } = useHover({ isDisabled: mergedSettings.isDisabled });
  const { focusProps, isFocus } = useFocus({ isDisabled: mergedSettings.isDisabled });
  const { activeProps, isActive } = useActive({ isDisabled: mergedSettings.isDisabled });
  const actionableProps = mergeProps(pressProps, hoverProps, activeProps, focusProps);
  return { actionableProps, isActive, isHover, isFocus };
}
